import React from 'react';
import { useNode } from '@craftjs/core';
import { useTheme } from '../../../contexts/ThemeContext';
import { Youtube } from 'lucide-react';

export const VideoBlock = ({ url, width }) => {
  const { isDark } = useTheme();
  const { connectors: { connect, drag }, selected, actions: { setProp } } = useNode((state) => ({
    selected: state.events.selected
  }));

  // Extract YouTube video ID
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
    return match ? match[1] : null;
  };

  const videoId = getYouTubeId(url);

  return (
    <div
      ref={(ref) => ref && connect(drag(ref))}
      className={`p-4 rounded-lg transition-all ${
        selected ? 'ring-2 ring-[#A2FF00]' : ''
      } ${isDark ? 'hover:bg-zinc-800/50' : 'hover:bg-gray-100'}`}
    >
      {videoId ? (
        <div className="flex justify-center">
          <div style={{ width: width || '100%', maxWidth: '100%' }}>
            <div className="relative pb-[56.25%] h-0">
              <iframe
                src={`https://www.youtube.com/embed/${videoId}`}
                className="absolute top-0 left-0 w-full h-full rounded-lg"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="YouTube video"
              />
            </div>
          </div>
        </div>
      ) : (
        <div
          className={`flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg ${
            isDark ? 'border-zinc-600' : 'border-gray-300'
          }`}
        >
          <Youtube className={`h-12 w-12 mb-2 ${isDark ? 'text-zinc-500' : 'text-gray-400'}`} />
          <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>Add YouTube URL in settings</p>
        </div>
      )}
    </div>
  );
};

VideoBlock.craft = {
  displayName: 'Video',
  props: {
    url: '',
    width: '100%'
  },
  related: {
    toolbar: VideoBlockSettings
  }
};

function VideoBlockSettings() {
  const { actions: { setProp }, props } = useNode((node) => ({
    props: node.data.props
  }));
  const { isDark } = useTheme();

  return (
    <div className="space-y-4">
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          YouTube URL
        </label>
        <input
          type="text"
          value={props.url}
          onChange={(e) => setProp(props => props.url = e.target.value)}
          placeholder="https://youtube.com/watch?v=..."
          className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'}`}
        />
      </div>
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Width
        </label>
        <select
          value={props.width}
          onChange={(e) => setProp(props => props.width = e.target.value)}
          className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'}`}
        >
          <option value="50%">Small (50%)</option>
          <option value="75%">Medium (75%)</option>
          <option value="100%">Full Width</option>
        </select>
      </div>
    </div>
  );
}

export { VideoBlockSettings };